/**
 * Spinner Component
 * Loading indicator
 */

import React, { forwardRef } from 'react';
import { Box } from '../primitives/Box';
import { keyframes } from '@emotion/react';

/**
 * Spinner Component
 * 
 * @example
 * <Spinner size="lg" color="brand.500" />
 */
export const Spinner = forwardRef(({
  size = 'md',
  thickness = '2px',
  speed = '0.65s',
  emptyColor = 'transparent',
  color,
  label = 'Loading...',
  ...props
}, ref) => {
  const sizeMap = {
    xs: '0.75rem',
    sm: '1rem',
    md: '1.5rem',
    lg: '2rem',
    xl: '3rem',
  };

  const spinnerSize = sizeMap[size] || size;

  return (
    <Box
      ref={ref}
      as="span"
      display="inline-block"
      borderColor="currentColor"
      borderStyle="solid"
      borderRadius="full"
      borderWidth={thickness}
      borderBottomColor={emptyColor}
      borderLeftColor={emptyColor}
      color={color || 'brand.500'}
      w={spinnerSize}
      h={spinnerSize}
      animation={`spin ${speed} linear infinite`}
      {...props}
    >
      {label && (
        <span
          style={{
            border: 0,
            clip: 'rect(0, 0, 0, 0)',
            height: '1px',
            margin: '-1px',
            overflow: 'hidden',
            padding: 0,
            position: 'absolute',
            width: '1px',
            whiteSpace: 'nowrap',
          }}
        >
          {label}
        </span>
      )}
    </Box>
  );
});

Spinner.displayName = 'Spinner';

// Add keyframe animation
if (typeof document !== 'undefined') {
  const styleId = 'chakra-spinner-styles';
  if (!document.getElementById(styleId)) {
    const style = document.createElement('style');
    style.id = styleId;
    style.textContent = `
      @keyframes spin {
        from { transform: rotate(0deg); }
        to { transform: rotate(360deg); }
      }
    `;
    document.head.appendChild(style);
  }
}

export default Spinner;
